import { events, publish } from "@theme/event-bus";
import { sectionStatusHandler, text } from "@theme/fetch";
import { isUntilBreakpoint, parseHtml, updateQueryParam } from "@theme/helpers";
import { HTMLCustomElement } from "@theme/html-custom-element";

customElements.define(
  "main-product",
  class MainProduct extends HTMLCustomElement {
    requiredAttributes = ["section-id"];

    #stickyAddToCart = null;
    #stickySourceHTML = "";
    #stickySourceObserver = null;
    #observedStickySource = null;
    #stickySyncFrame = null;

    #onStickyAddToCartScroll = () => this.#syncStickyAddToCart();

    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();

      this.on(
        "option-change",
        this.refs.form,
        this.handleOptionChange.bind(this),
      );
      this.on("scroll", window, this.#onStickyAddToCartScroll, {
        passive: true,
      });
      this.on("resize", window, this.#onStickyAddToCartScroll, {
        passive: true,
      });
      requestAnimationFrame(() => this.#syncStickyAddToCart());
    }

    disconnectedCallback() {
      this.#stickySourceObserver?.disconnect();
      this.#stickySourceObserver = null;
      this.#observedStickySource = null;
      this.#stickyAddToCart = null;
      this.#stickySourceHTML = "";
      if (this.#stickySyncFrame) {
        cancelAnimationFrame(this.#stickySyncFrame);
        this.#stickySyncFrame = null;
      }
      if (super.disconnectedCallback) super.disconnectedCallback();
    }

    /**
     * Show a mobile sticky add-to-cart clone only while the in-page buttons
     * are still below it. Once the user reaches them, keep it hidden until
     * they scroll back above.
     */
    #syncStickyAddToCart() {
      const source = this.#getStickyAddToCartSource();
      if (!source) return;

      if (!isUntilBreakpoint("medium")) {
        this.#setStickyAddToCartVisible(false);
        return;
      }

      const sticky = this.#ensureStickyAddToCart(source);
      this.#observeStickySource(source);
      this.#syncStickyCloneContent(source, sticky);

      const sourceRect = source.getBoundingClientRect();
      const stickyRect = sticky.getBoundingClientRect();
      const sourceStillBelow = sourceRect.top > stickyRect.top;

      this.#setStickyAddToCartVisible(sourceStillBelow);
    }

    /**
     * @returns {HTMLElement | null}
     */
    #getStickyAddToCartSource() {
      return this.querySelector(
        ".product-buttons__buttons:not(.product-buttons__buttons--sticky)",
      );
    }

    /**
     * @param {HTMLElement} source
     * @returns {HTMLElement}
     */
    #ensureStickyAddToCart(source) {
      if (this.#stickyAddToCart?.isConnected) return this.#stickyAddToCart;

      const sticky = source.cloneNode(true);
      sticky.classList.add("product-buttons__buttons--sticky");
      sticky.setAttribute("aria-hidden", "true");
      sticky.toggleAttribute("inert", true);
      this.#sanitizeStickyClone(sticky);

      sticky.addEventListener("click", (event) => {
        const button = event.target.closest("button");
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();

        const source = this.#getStickyAddToCartSource();
        if (!source) return;

        const stickyButtons = [...sticky.querySelectorAll("button")];
        const sourceButtons = [...source.querySelectorAll("button")];
        sourceButtons[stickyButtons.indexOf(button)]?.click();
      });

      this.append(sticky);
      this.#stickyAddToCart = sticky;
      this.#stickySourceHTML = "";
      return sticky;
    }

    /**
     * @param {HTMLElement} sticky
     */
    #sanitizeStickyClone(sticky) {
      sticky
        .querySelectorAll(
          ".customily-share-customization-button, .main-product__delivery-dates, .product-buttons__sticky-meta",
        )
        .forEach((element) => {
          element.remove();
        });
      sticky.querySelectorAll("[id]").forEach((element) => {
        element.removeAttribute("id");
      });
      sticky.querySelectorAll("[data-ref]").forEach((element) => {
        element.removeAttribute("data-ref");
      });
      sticky.querySelectorAll("button").forEach((button) => {
        button.setAttribute("type", "button");
        button.removeAttribute("name");
      });
    }

    /**
     * @param {HTMLElement} source
     * @param {HTMLElement} sticky
     */
    #syncStickyCloneContent(source, sticky) {
      if (this.#stickySourceHTML !== source.innerHTML) {
        this.#stickySourceHTML = source.innerHTML;
        sticky.innerHTML = source.innerHTML;
        this.#sanitizeStickyClone(sticky);
      }

      this.#syncStickyMeta(sticky);
    }

    /**
     * @param {HTMLElement} sticky
     */
    #syncStickyMeta(sticky) {
      const titleText =
        this.querySelector(".main-product__title")?.textContent?.trim() ?? "";
      const priceSource = this.refs.form?.querySelector(".product-price");

      let meta = sticky.querySelector(".product-buttons__sticky-meta");
      if (!meta) {
        meta = document.createElement("div");
        meta.className = "product-buttons__sticky-meta";
        meta.innerHTML =
          '<p class="product-buttons__sticky-title"></p><div class="product-buttons__sticky-price"></div>';
        sticky.prepend(meta);
      }

      const title = meta.querySelector(".product-buttons__sticky-title");
      if (title) title.textContent = titleText;

      const priceTarget = meta.querySelector(".product-buttons__sticky-price");
      if (!priceTarget) return;

      priceTarget.replaceChildren();
      if (!priceSource) return;

      const currentPrice = priceSource.querySelector(".product-price__price");
      const compareAt = priceSource.querySelector("s");
      if (currentPrice) {
        priceTarget.append(currentPrice.cloneNode(true));
      }
      if (compareAt) {
        priceTarget.append(compareAt.cloneNode(true));
      }
    }

    /**
     * @param {HTMLElement} source
     */
    #observeStickySource(source) {
      if (this.#observedStickySource === source) return;

      if (!this.#stickySourceObserver) {
        this.#stickySourceObserver = new MutationObserver(() => {
          if (this.#stickySyncFrame) return;
          this.#stickySyncFrame = requestAnimationFrame(() => {
            this.#stickySyncFrame = null;
            this.#stickySourceHTML = "";
            this.#syncStickyAddToCart();
          });
        });
      } else {
        this.#stickySourceObserver.disconnect();
      }

      this.#observedStickySource = source;
      this.#stickySourceObserver.observe(source, {
        attributes: true,
        attributeFilter: ["disabled", "class"],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    /**
     * @param {boolean} visible
     */
    #setStickyAddToCartVisible(visible) {
      const sticky = this.#stickyAddToCart;
      if (!sticky) return;

      sticky.classList.toggle("is-visible", visible);
      sticky.toggleAttribute("inert", !visible);
      sticky.toggleAttribute("aria-hidden", !visible);
    }

    /**
     * Handles the product form option change event.
     * @param {object} event - The event payload.
     */
    async handleOptionChange(event) {
      const response = await this.getSectionByOptionValues(event.detail);
      const section = parseHtml(response, this.tagName.toLowerCase());

      publish(events.sectionUpdate, {
        sections: {
          [this.getAttribute("section-id")]: response,
        },
        onAfterElementsUpdated: () => {
          this.#stickySourceHTML = "";
          this.#syncStickyAddToCart();

          const preview =
            window.engraver?.currentPreview || window.engraver?.defaultPreview;
          const hasPreviewContent = Boolean(
            preview?.imagePath ||
            preview?.textsPreview?.length ||
            preview?.circularTextsPreview?.length ||
            preview?.imagePlaceHoldersPreview?.length ||
            preview?.vectorEPSPreview?.length,
          );
          if (hasPreviewContent) {
            window.dispatchEvent(
              new CustomEvent("customily-live-preview-show-canvas"),
            );
          }
        },
      });

      updateQueryParam("variant", section.getAttribute("selected-variant"));
    }

    /**
     * Fetches and returns the updated section.
     * @param {string[]} values - The selected option values.
     * @returns {Promise<string>}
     */
    getSectionByOptionValues(values) {
      return text(
        fetch(
          `${window.location.pathname}?section_id=${this.getAttribute("section-id")}&option_values=${values.join(",")}`,
        ),
        sectionStatusHandler,
      );
    }
  },
);
